// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/LeadershipSection.js

import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2, FiX, FiImage } from 'react-icons/fi';
import api from '../../../api';

const LeadershipSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  const leadership = websiteData?.leadership || { items: [] };

  // Local state vectors to insulate text field values during live input typing
  const [localTitle, setLocalTitle] = useState(leadership.sectionTitle || '');
  const [localSubtitle, setLocalSubtitle] = useState(leadership.sectionSubtitle || '');
  const [localDisplayStyle, setLocalDisplayStyle] = useState(leadership.displayStyle || 'grid');
  const [localCheckboxes, setLocalCheckboxes] = useState({
    showSocialLinks: leadership.showSocialLinks !== false,
    showResearchInterests: leadership.showResearchInterests !== false
  });
  const [localItems, setLocalItems] = useState(leadership.items || []);

  const [newItem, setNewItem] = useState({
    name: '',
    designation: '',
    qualifications: '',
    experience: '',
    email: '',
    message: '',
    researchInterests: []
  });
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);

  // Sync back from parent objects properties only on standard initial load arrays modification hooks
  useEffect(() => {
    console.log('[LeadershipSection] Initializing state snapshot fields from master source parameters.');
    const sec = websiteData?.leadership || {};
    setLocalTitle(sec.sectionTitle || '');
    setLocalSubtitle(sec.sectionSubtitle || '');
    setLocalDisplayStyle(sec.displayStyle || 'grid');
    setLocalCheckboxes({
      showSocialLinks: sec.showSocialLinks !== false,
      showResearchInterests: sec.showResearchInterests !== false
    });
    setLocalItems(sec.items || []);
  }, [websiteData?.leadership, tenantId]);

  const handleToggle = () => {
    handleInputChange('leadership', 'isActive', !leadership.isActive);
  };

  const handleGlobalFieldChange = (field, value) => {
    console.log(`[LeadershipSection] Setting isolated config: Key [${field}] -> Value [${value}]`);
    if (field === 'sectionTitle') setLocalTitle(value);
    if (field === 'sectionSubtitle') setLocalSubtitle(value);
    if (field === 'displayStyle') setLocalDisplayStyle(value);
  };

  const handleLocalCheckboxToggle = (key) => {
    setLocalCheckboxes(prev => {
      const nextState = { ...prev, [key]: !prev[key] };
      console.log(`[LeadershipSection] Adjusting configuration toggle option [${key}]:`, nextState[key]);
      return nextState;
    });
  };

  const handleAddItem = () => {
    if (!newItem.name || !newItem.designation) return;
    
    const item = {
      _id: Date.now().toString(),
      name: newItem.name,
      designation: newItem.designation,
      qualifications: newItem.qualifications,
      experience: newItem.experience,
      email: newItem.email,
      message: newItem.message,
      researchInterests: newItem.researchInterests,
      image: ''
    };
    
    const updatedItems = [...localItems, item];
    console.log('[LeadershipSection] Inserting structural card layout locally:', item);
    setLocalItems(updatedItems);
    
    // Push changes up to maintain synchronization layer array visibility parameters
    handleInputChange('leadership', 'items', updatedItems);
    setNewItem({
      name: '',
      designation: '',
      qualifications: '',
      experience: '',
      email: '',
      message: '',
      researchInterests: []
    });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = localItems.filter((_, i) => i !== index);
    console.log('[LeadershipSection] Extracting row block entry position location:', index);
    setLocalItems(updatedItems);
    
    // Push changes up to maintain synchronization layer array visibility parameters
    handleInputChange('leadership', 'items', updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    console.log(`[LeadershipSection] Modifying row value locally -> Index [${index}] -> Key [${field}]`);
    setLocalItems(updatedItems);
  };

  const handleImageUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('Image size must be less than 2MB');
      event.target.value = '';
      return;
    }

    const localBlobPreview = URL.createObjectURL(file);
    const previewItems = [...localItems];
    previewItems[index] = { ...previewItems[index], image: localBlobPreview };
    setLocalItems(previewItems);

    setUploading(prev => ({ ...prev, [index]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', 'leadership');
      formData.append('itemIndex', index);
      formData.append('subField', 'image');

      const response = await api.post(`/api/website/${tenantId}/upload/leadership`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const fileUrl = response.data.data?.url || response.data.data;
        console.log('[LeadershipSection] Remote address source path resolved successfully:', fileUrl);
        const updatedItems = [...localItems];
        updatedItems[index] = { ...updatedItems[index], image: fileUrl };
        setLocalItems(updatedItems);
        handleInputChange('leadership', 'items', updatedItems);
      } else {
        alert(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(prev => ({ ...prev, [index]: false }));
      event.target.value = '';
    }
  };

  const resolveAssetUrl = (urlPath) => {
    if (!urlPath) return '';
    if (typeof urlPath === 'string' && (urlPath.includes('tenants%2Fnull') || urlPath.includes('tenants/null'))) {
      return '';
    }
    const base = (api?.defaults?.baseURL || '').replace(/\/+$/, '') || (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (typeof urlPath === 'object') {
      if (urlPath.url) return urlPath.url.startsWith('/') ? (base ? `${base}${urlPath.url}` : urlPath.url) : urlPath.url;
      if (urlPath.fileId) return base ? `${base}/api/files/tenant-website/${urlPath.fileId}` : `/api/files/tenant-website/${urlPath.fileId}`;
      return '';
    }
    if (/^https?:\/\//i.test(urlPath)) return urlPath;
    if (urlPath.startsWith('/')) return base ? `${base}${urlPath}` : urlPath;
    return urlPath;
  };

  const handleRemoveImage = (index) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], image: '' };
    setLocalItems(updatedItems);
    handleInputChange('leadership', 'items', updatedItems);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[LeadershipSection] Broadcasting all local values and executing master save transaction layout configurations.');
    
    try {
      // Direct pass unified payload configuration arrays down into parent variables map snapshots
      await handleInputChange('leadership', 'sectionTitle', localTitle);
      await handleInputChange('leadership', 'sectionSubtitle', localSubtitle);
      await handleInputChange('leadership', 'displayStyle', localDisplayStyle);
      await handleInputChange('leadership', 'showSocialLinks', localCheckboxes.showSocialLinks);
      await handleInputChange('leadership', 'showResearchInterests', localCheckboxes.showResearchInterests);
      await handleInputChange('leadership', 'items', localItems);

      const assembledPayload = {
        ...leadership,
        sectionTitle: localTitle,
        sectionSubtitle: localSubtitle,
        displayStyle: localDisplayStyle,
        showSocialLinks: localCheckboxes.showSocialLinks,
        showResearchInterests: localCheckboxes.showResearchInterests,
        items: localItems
      };

      await handleSave('leadership', assembledPayload);
    } catch (error) {
      console.error('Failed to save leadership team:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Top Header Panel Row */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Leadership Team</h2>
            <p className="text-sm text-slate-500 mt-0.5">Showcase academic and management institutional visionaries</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button
              type="button"
              onClick={handleToggle}
              className={`flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                leadership.isActive !== false
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              {leadership.isActive !== false ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleSaveClick}
              disabled={saving}
              className={`flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Settings Configuration Card Container */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Section Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
              <input
                type="text"
                value={localTitle}
                onChange={(e) => handleGlobalFieldChange('sectionTitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Our Leadership"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle Descriptive Note</label>
              <input
                type="text"
                value={localSubtitle}
                onChange={(e) => handleGlobalFieldChange('sectionSubtitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Visionaries driving excellence"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Display Layout Style</label>
              <select
                value={localDisplayStyle}
                onChange={(e) => handleGlobalFieldChange('displayStyle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none"
              >
                <option value="grid">Grid Layout Map</option>
                <option value="carousel">Carousel View Slider</option>
              </select>
            </div>

            <div className="md:col-span-2 bg-slate-50 rounded-xl p-3 border border-slate-150 grid grid-cols-2 gap-3">
              {[
                { label: 'Show Social Connections Links', key: 'showSocialLinks', checked: localCheckboxes.showSocialLinks },
                { label: 'Show Research Interests List', key: 'showResearchInterests', checked: localCheckboxes.showResearchInterests },
              ].map((checkbox) => (
                <label key={checkbox.key} className="flex items-center space-x-2.5 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={checkbox.checked}
                    onChange={() => handleLocalCheckboxToggle(checkbox.key)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all border ${
                    checkbox.checked 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'bg-white border-slate-300 text-transparent group-hover:border-slate-400'
                  }`}>
                    <span className={`w-2 h-2 rounded-sm bg-white ${checkbox.checked ? 'block' : 'hidden'}`} />
                  </div>
                  <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                    {checkbox.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Existing Profiles Mapping Frame List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-sm font-bold text-slate-800">Leadership Members</h3>
            <span className="text-xs font-semibold bg-slate-200 text-slate-600 px-2.5 py-0.5 rounded-full">
              {localItems.length} Executives
            </span>
          </div>

          <div className="space-y-4">
            {localItems.map((item, index) => {
              const resolvedImage = resolveAssetUrl(item.image);

              return (
                <div key={item._id || index} className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                  <div className="p-4 sm:p-5 space-y-4">
                    
                    {/* Visual Media Picker Block Container */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-slate-50 p-3 rounded-xl border border-slate-150">
                      <div className="flex-shrink-0 mx-auto sm:mx-0">
                        <div className="w-16 h-16 rounded-full border border-slate-200 flex items-center justify-center overflow-hidden bg-white shadow-inner relative group">
                          {resolvedImage ? (
                            <div className="w-full h-full relative">
                              <img 
                                src={resolvedImage} 
                                alt={item.name || "Leader Profile Photo"} 
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(index)}
                                className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white rounded-full"
                              >
                                <FiX className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <FiImage className="h-6 w-6 text-slate-400" />
                          )}
                        </div>
                      </div>
                      <div className="text-center sm:text-left flex-1">
                        <input
                          type="file"
                          id={`leader-image-${index}`}
                          accept="image/jpeg,image/png,image/webp"
                          onChange={(e) => handleImageUpload(e, index)}
                          disabled={uploading[index]}
                          className="hidden"
                        />
                        <label
                          htmlFor={`leader-image-${index}`}
                          className={`inline-flex items-center px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 shadow-sm cursor-pointer transition-all select-none ${
                            uploading[index] ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                          }`}
                        >
                          {uploading[index] ? 'Uploading...' : (resolvedImage ? 'Change Photo' : 'Upload Photo')}
                        </label>
                        <p className="text-[10px] text-slate-400 mt-1">Accepts JPG, PNG, WEBP under 2MB</p>
                      </div>
                    </div>

                    {/* Form Fields Grid Setup */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Full Name</span>
                        <input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                          placeholder="Full Name"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Designation</span>
                        <input
                          type="text"
                          value={item.designation || ''}
                          onChange={(e) => handleItemChange(index, 'designation', e.target.value)}
                          placeholder="Role title"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Academic Degrees & Qualifications</span>
                        <input
                          type="text"
                          value={item.qualifications || ''}
                          onChange={(e) => handleItemChange(index, 'qualifications', e.target.value)}
                          placeholder="e.g., Ph.D., M.Tech"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Professional Experience Summary</span>
                        <input
                          type="text"
                          value={item.experience || ''}
                          onChange={(e) => handleItemChange(index, 'experience', e.target.value)}
                          placeholder="e.g., 15+ years in academic administration"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Official Contact Email</span>
                        <input
                          type="text"
                          value={item.email || ''}
                          onChange={(e) => handleItemChange(index, 'email', e.target.value)}
                          placeholder="e.g., principal@institution.com"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Leadership Address / Statement Note</span>
                        <textarea
                          value={item.message || ''}
                          onChange={(e) => handleItemChange(index, 'message', e.target.value)}
                          placeholder="Type address transcript or milestone message note..."
                          rows="3"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500 resize-y"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Research Domains & Interest Subspaces (comma split)</span>
                        <input
                          type="text"
                          value={item.researchInterests?.join(', ') || ''}
                          onChange={(e) => handleItemChange(index, 'researchInterests', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                          placeholder="e.g., Machine Learning, Quantum Computing"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="mt-2 flex justify-end border-t border-slate-100 pt-2">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-rose-500 hover:text-rose-700 font-semibold text-xs flex items-center px-2 py-1 rounded-md hover:bg-rose-50 transition-colors"
                      >
                        <FiTrash2 className="mr-1 h-3.5 w-3.5" />
                        Delete Member
                      </button>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>

          {/* Add Item Action Form alignments */}
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm mt-4">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1 bg-blue-50 rounded-md text-blue-600">
                <FiPlus className="h-4 w-4 stroke-[3]" />
              </div>
              <h4 className="text-sm font-bold text-slate-800">Add New Executive Leader</h4>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  placeholder="Full Name *"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  value={newItem.designation}
                  onChange={(e) => setNewItem({ ...newItem, designation: e.target.value })}
                  placeholder="Designation * (e.g., Director, Principal)"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 gap-3">
                <input
                  type="text"
                  value={newItem.qualifications}
                  onChange={(e) => setNewItem({ ...newItem, qualifications: e.target.value })}
                  placeholder="Qualifications (e.g., Ph.D, M.Tech)"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  value={newItem.experience}
                  onChange={(e) => setNewItem({ ...newItem, experience: e.target.value })}
                  placeholder="Experience Summary (e.g., 20+ years in Research & Administration)"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-end justify-end pt-1">
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 bg-slate-900 text-white font-semibold rounded-lg text-sm hover:bg-slate-800 transition-all active:scale-[0.98] shadow-sm whitespace-nowrap"
                >
                  Insert Executive
                </button>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default LeadershipSection;